import type {
  AutocompleteGroup,
  AutocompleteItem,
  DisplayStructure,
} from "./Autocomplete.type";

/**
 * Checks if the provided options are grouped options or flat options
 */
export const isGroupedOptions = <T>(
  options: T[] | AutocompleteGroup<T>[],
): options is AutocompleteGroup<T>[] => {
  return (
    options.length > 0 &&
    options[0] != null &&
    typeof options[0] === "object" &&
    "options" in options[0]
  );
};

/**
 * Creates autocomplete items from options, optionally including a "none" option
 */
export const createItems = <T, TValue>(
  options: T[] | AutocompleteGroup<T>[],
  noneOptionText: string,
  getOptionLabel: (option: T) => string,
  getOptionValue: (option: T) => TValue,
  getGroupLabel: (group: AutocompleteGroup<T>) => string,
  defaultToNone = true,
): AutocompleteItem<TValue>[] => {
  const result: AutocompleteItem<TValue>[] = [];

  if (defaultToNone) {
    result.push({
      id: "__none__",
      label: noneOptionText,
      value: null as TValue | null,
      isNoneOption: true,
    });
  }

  if (isGroupedOptions(options)) {
    for (const group of options) {
      for (const option of group.options) {
        result.push({
          id: String(getOptionValue(option)),
          label: getOptionLabel(option),
          value: getOptionValue(option),
          isNoneOption: false,
          group: getGroupLabel(group),
        });
      }
    }
  } else {
    for (const option of options) {
      result.push({
        id: String(getOptionValue(option)),
        label: getOptionLabel(option),
        value: getOptionValue(option),
        isNoneOption: false,
      });
    }
  }

  return result;
};

/**
 * Creates a display structure separating grouped and ungrouped items
 */
export const createDisplayStructure = <TValue>(
  items: AutocompleteItem<TValue>[],
): DisplayStructure<TValue> => {
  const ungrouped: AutocompleteItem<TValue>[] = [];
  const grouped: Record<string, AutocompleteItem<TValue>[]> = {};

  for (const item of items) {
    if (item.group) {
      let groupArray = grouped[item.group];
      if (!groupArray) {
        groupArray = grouped[item.group] = [];
      }
      groupArray.push(item);
    } else {
      ungrouped.push(item);
    }
  }

  return { ungrouped, grouped };
};
